import { Component, ComponentInterface, Element, Event, EventEmitter, h, Host, Method, Prop } from '@stencil/core';
import { addEventListener, removeEventListener } from '../../utils/helpers';
import { ModalChangeEventDetail } from './vds-modal.interface';

@Component({
  tag: 'vds-modal',
  styleUrl: 'vds-modal.scss',
  shadow: true,
})
export class VdsModal implements ComponentInterface {

  private modalIndex: number;

  private backdropElement?: HTMLElement;

  private dialogElement?: HTMLElement;

  private closeButtonElements?: HTMLElement[];

  @Element() el!: HTMLVdsModalElement;

  @Prop() headline: string;

  @Prop() header = false;

  @Prop() footer = false;

  @Prop({ reflect: true, mutable: true }) open = false;

  @Prop() stack = false;

  @Prop() lightbox = false;

  @Prop() hotspot = false;

  @Prop() multipleSlides = false;

  @Event() public vdsModalShown!: EventEmitter<ModalChangeEventDetail>;

  @Event() public vdsModalHidden!: EventEmitter<ModalChangeEventDetail>;

  constructor() {
    this.modalIndex = this.getModalIndex();
  }

  @Method()
  async openModal(reason?: string): Promise<void> {
    if (!this.stack) {
      this.closeOtherModals();
    }

    this.backdropElement.classList.add('flex');

    new Promise<void>((resolve) => {
      setTimeout(() => {
        this.dialogElement.classList.add('open');
        this.backdropElement.classList.add('open');
        resolve();
      });
    }).then(() => {
      setTimeout(() => {
        this.vdsModalShown.emit({reason});
      }, 300);
    });
  }

  @Method()
  async closeModal(reason?: string): Promise<void> {
    this.dialogElement.classList.remove('open');
    this.backdropElement.classList.remove('open');

    new Promise<void>((resolve) => {
      setTimeout(() => {
        resolve()
      });
    }).then(() => {
      setTimeout(() => {
        if(!this.backdropElement.classList.contains('open')) {
          this.backdropElement.classList.remove('flex');
        }
        this.vdsModalHidden.emit({reason});
      }, 300);
    });
  }

  render() {

    return (
      <Host
      aria-labelledby="modal-title"
      role="dialog"
      aria-modal="true"
      class={{ 'lightbox': this.lightbox }}>
        <div
          class="modal backdrop"
          style={{ zIndex: `${10000 + this.modalIndex}` }}
          ref={element => (this.backdropElement = element)}
          onClick={(e: Event) => (e.target === this.backdropElement ? this.closeModal() : e.preventDefault())}
        >
          <div
          ref={element => (this.dialogElement = element)}
          class={{ 'dialog': true }}>

            {this.header && (
              <div class="header" part="header">
                <slot name="header"></slot>
              </div>
            )}


            {!this.header && (
              <button
                type="button"
                class={{
                  'close': true,
                  'no-header': !this.header,
                }}
                onClick={() => this.closeModal()}
              >
                {this.renderCloseSVG()}
              </button>
            )}

            <div class="body" part="body">
              <slot name="body"></slot>
            </div>

            {this.footer && (
              <div class="footer" part="footer">
                <slot name="footer"></slot>
              </div>
            )}
          </div>

        </div>
      </Host>
    );
  }

  disconnectedCallback(): void {
    this.closeButtonElements?.forEach(element => removeEventListener(element, 'click', () => this.closeModal()));
  }

  componentWillRender(): void | Promise<void> {
    this.appendToBody();
  }

  componentDidRender(): void {
    const closeButtonElements = this.closeButtonElements = Array.from(this.el.querySelectorAll('[data-modal-close]'));
    closeButtonElements.forEach(element => addEventListener(element, 'click', () => this.closeModal(element.dataset.modalClose)));

    if (this.open) {
      this.openModal();
    }
  }

  private getModalIndex(): number {
    const modals = Array.from(document.querySelectorAll('vds-modal'));
    const index = modals.findIndex(modal => modal === this.el);
    return index;
  }

  private appendToBody(): void {
    const body = document.querySelector('body');
    body.append(this.el);
  }

  private closeOtherModals(): void {
    const modals = Array
      .from(document.querySelectorAll('vds-modal'))
      .filter(modal => modal !== this.el);

    modals.forEach(modal => modal.closeModal());
  }

  private renderCloseSVG(): string {
    return (
      <svg viewBox="51 51 26 26" width="26" height="26">
        <path d="M76 52 64 64" class="cls-1"></path>
        <path d="M76 76 52 52" class="cls-1"></path>
        <path d="M60 68 52 76" class="cls-1"></path>
      </svg>
    );
  }

}
